
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadEngineState, resolveFlagConfig } from "../utils/mcEngineData";

function money(value) {
  const n = Number(value || 0);
  const sign = n < 0 ? "-" : "";
  return `${sign}R${Math.abs(n).toFixed(2)}`;
}

function safeText(value) {
  return value == null ? "" : String(value);
}

function safeFilePart(value, fallback = "MC") {
  const text = safeText(value).trim() || fallback;
  return text
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || fallback;
}

function getSessionDateCode(session = {}, sheet = {}) {
  const idText = [session.id, session.sessionId, sheet.id, sheet.sessionId].map(safeText).join(" ");
  const match = idText.match(/(?:MOS-)?MC-(\d{6})/i) || idText.match(/\b(\d{6})\b/);
  if (match?.[1]) return match[1];

  const rawDate = session.completedAt || session.updatedAt || session.startedAt || sheet.createdAt || new Date().toISOString();
  const date = new Date(rawDate);
  if (!Number.isNaN(date.getTime())) {
    const yy = String(date.getFullYear()).slice(-2);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yy}${mm}${dd}`;
  }

  return "000000";
}

function getSiteName(session = {}, sheet = {}) {
  return safeFilePart(sheet.storeName || session.storeName || session.store || "Site-Name", "Site-Name");
}

function getSectionName(session = {}) {
  const sectionName =
    session.sectionName ||
    session.section ||
    session.countSection ||
    session.scopeName ||
    "Full Count";
  return safeFilePart(sectionName, "Full-Count");
}

function getCountNote(row = {}) {
  return safeText(row.notes || row.note || row.countNote || row.flagNote || row.comment || row.comments);
}

function normText(value) {
  return safeText(value).trim().toLowerCase().replace(/\s+/g, " ");
}

function getCategoryOrder(session = {}, options = {}) {
  const scoped = options?.engineState || loadEngineState(session.storeName || session.store || "ALL") || loadEngineState("ALL") || {};
  return Array.isArray(options?.categoryOrder)
    ? options.categoryOrder
    : Array.isArray(scoped?.config?.categoryOrder)
      ? scoped.config.categoryOrder
      : [];
}

function getCategoryGroups(session = {}, options = {}) {
  const scoped = options?.engineState || loadEngineState(session.storeName || session.store || "ALL") || loadEngineState("ALL") || {};
  return Array.isArray(options?.categoryGroups)
    ? options.categoryGroups
    : Array.isArray(scoped?.config?.categoryGroups)
      ? scoped.config.categoryGroups
      : [];
}

function resolveGroupName(category, groups = []) {
  const cleanCategory = normText(category);
  const found = (groups || []).find((group) => (group?.outputs || []).some((output) => normText(output) === cleanCategory));
  return safeText(found?.name || category || "Other");
}

function sortRowsByGroupAndCategory(rows = [], session = {}, options = {}) {
  const groups = getCategoryGroups(session, options);
  const categoryOrder = getCategoryOrder(session, options);
  const groupRank = new Map((groups || []).map((group, index) => [normText(group?.name), index]));
  const categoryRank = new Map((categoryOrder || []).map((category, index) => [normText(category), index]));
  return [...rows].sort((a, b) => {
    const ag = resolveGroupName(a.category, groups);
    const bg = resolveGroupName(b.category, groups);
    const agr = groupRank.has(normText(ag)) ? groupRank.get(normText(ag)) : 99999;
    const bgr = groupRank.has(normText(bg)) ? groupRank.get(normText(bg)) : 99999;
    if (agr !== bgr) return agr - bgr;
    const acr = categoryRank.has(normText(a.category)) ? categoryRank.get(normText(a.category)) : 99999;
    const bcr = categoryRank.has(normText(b.category)) ? categoryRank.get(normText(b.category)) : 99999;
    if (acr !== bcr) return acr - bcr;
    return safeText(a.product).localeCompare(safeText(b.product));
  });
}

function buildGroupedTableBody(rows = [], session = {}, options = {}, rowBuilder, columnCount) {
  const groups = getCategoryGroups(session, options);
  const sorted = sortRowsByGroupAndCategory(rows, session, options);
  const body = [];
  let currentGroup = null;

  sorted.forEach((row) => {
    const groupName = resolveGroupName(row.category, groups);
    if (groupName !== currentGroup) {
      currentGroup = groupName;
      body.push([{ content: groupName, colSpan: columnCount, styles: { fillColor: [230, 236, 245], textColor: [15, 23, 42], fontStyle: "bold", fontSize: 10, cellPadding: { top: 3.2, right: 2, bottom: 3.2, left: 2 } } }]);
    }
    body.push(rowBuilder(row));
  });

  return body;
}


function normalizeNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function normalizeOrderValue(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const cleaned = raw.replace(/,/g, ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function isOrderedRow(row = {}) {
  return normalizeOrderValue(row.order ?? row.orderQty ?? row.ordered ?? row.mosOrder ?? row.orderValue) > 0;
}

function resolveMosBooleanOption(optionValue, key, scopes = []) {
  if (optionValue === true) return true;
  if (optionValue === false) return false;
  for (const scope of scopes) {
    const cfg = resolveFlagConfig(scope || 'ALL');
    if (cfg && cfg[key] === true) return true;
  }
  return false;
}

function orderCell(value, row = {}) {
  return isOrderedRow(row)
    ? { content: safeText(value), styles: { fontStyle: "bold", textColor: [8, 18, 32] } }
    : safeText(value);
}

function collectTopLosses(rows = []) {
  return rows
    .map((row) => ({
      label: safeText(row.category || row.product || "—"),
      value: normalizeNumber(row.costVariance),
    }))
    .filter((row) => row.value < 0)
    .sort((a, b) => a.value - b.value)
    .slice(0, 5);
}

function collectTopGains(rows = []) {
  return rows
    .map((row) => ({
      label: safeText(row.category || row.product || "—"),
      value: normalizeNumber(row.costVariance),
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);
}

function buildCategoryChartRows(rows = []) {
  const buckets = new Map();
  rows.forEach((row) => {
    const key = safeText(row.category || "Uncategorized");
    const next = (buckets.get(key) || 0) + normalizeNumber(row.costVariance);
    buckets.set(key, next);
  });

  return [...buckets.entries()]
    .map(([label, value]) => ({ label, value }))
    .filter((row) => row.value !== 0)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, 8);
}

function deriveSummaryData(summaryData = {}, rows = []) {
  const topLosses = Array.isArray(summaryData.topLosses) && summaryData.topLosses.length
    ? summaryData.topLosses
    : collectTopLosses(rows);

  const topGains = Array.isArray(summaryData.topGains) && summaryData.topGains.length
    ? summaryData.topGains
    : collectTopGains(rows);

  const chartRows = Array.isArray(summaryData.chartRows) && summaryData.chartRows.length
    ? summaryData.chartRows
    : buildCategoryChartRows(rows);

  const totalLoss = summaryData.totalLoss != null
    ? normalizeNumber(summaryData.totalLoss)
    : rows.reduce((sum, row) => {
        const val = normalizeNumber(row.costVariance);
        return val < 0 ? sum + val : sum;
      }, 0);

  const totalGain = summaryData.totalGain != null
    ? normalizeNumber(summaryData.totalGain)
    : rows.reduce((sum, row) => {
        const val = normalizeNumber(row.costVariance);
        return val > 0 ? sum + val : sum;
      }, 0);

  const netDifference = summaryData.netDifference != null
    ? normalizeNumber(summaryData.netDifference)
    : totalLoss + totalGain;

  const flaggedItems = summaryData.flaggedItems != null
    ? normalizeNumber(summaryData.flaggedItems)
    : rows.filter((row) => getCountNote(row).trim()).length;

  const varianceLines = summaryData.varianceLines != null
    ? normalizeNumber(summaryData.varianceLines)
    : rows.filter((row) => normalizeNumber(row.variance) !== 0).length;

  const cleanLines = summaryData.cleanLines != null
    ? normalizeNumber(summaryData.cleanLines)
    : Math.max(rows.length - varianceLines, 0);

  return {
    totalLoss,
    totalGain,
    netDifference,
    flaggedItems,
    varianceLines,
    cleanLines,
    topLosses,
    topGains,
    chartRows,
  };
}

function drawCard(pdf, x, y, w, h, title, value, valueColor = [255, 255, 255]) {
  pdf.setDrawColor(40, 68, 110);
  pdf.setFillColor(5, 12, 32);
  pdf.roundedRect(x, y, w, h, 4, 4, "FD");

  pdf.setTextColor(130, 170, 225);
  pdf.setFontSize(8);
  pdf.text(title.toUpperCase(), x + 5, y + 8);

  pdf.setTextColor(...valueColor);
  pdf.setFontSize(17);
  pdf.text(String(value), x + 5, y + 19);
}

function drawListCard(pdf, x, y, w, h, title, items, positive) {
  pdf.setDrawColor(40, 68, 110);
  pdf.setFillColor(5, 12, 32);
  pdf.roundedRect(x, y, w, h, 4, 4, "FD");

  pdf.setTextColor(130, 170, 225);
  pdf.setFontSize(9);
  pdf.text(title.toUpperCase(), x + 5, y + 8);

  const textColor = positive ? [120, 255, 180] : [255, 120, 120];
  pdf.setFontSize(9);

  items.slice(0, 5).forEach((item, index) => {
    const lineY = y + 18 + index * 11;
    pdf.setTextColor(190, 210, 235);
    pdf.text(`${index + 1}. ${safeText(item.label).slice(0, 24)}`, x + 5, lineY);

    pdf.setTextColor(...textColor);
    pdf.text(money(item.value), x + w - 5, lineY, { align: "right" });
  });

  if (!items.length) {
    pdf.setTextColor(150, 170, 195);
    pdf.text("No items", x + 5, y + 20);
  }
}

function drawBarChart(pdf, x, y, w, h, rows = []) {
  pdf.setDrawColor(40, 68, 110);
  pdf.setFillColor(5, 12, 32);
  pdf.roundedRect(x, y, w, h, 4, 4, "FD");

  pdf.setTextColor(130, 170, 225);
  pdf.setFontSize(9);
  pdf.text("TOP CATEGORY MOVEMENT", x + 5, y + 8);

  if (!rows.length) {
    pdf.setTextColor(150, 170, 195);
    pdf.text("No category movement", x + 5, y + 20);
    return;
  }

  const trimmedRows = rows.slice(0, 5);
  const maxAbs = Math.max(Math.abs(normalizeNumber(trimmedRows[0]?.value)), 1);
  const labelX = x + 5;
  const labelW = 34;
  const barX = x + 40;
  const valueX = x + w - 5;
  const barW = Math.max(18, w - 58);

  trimmedRows.forEach((row, index) => {
    const lineY = y + 18 + index * 14;
    const value = normalizeNumber(row.value);
    const ratio = Math.min(Math.abs(value) / maxAbs, 1);
    const fillW = Math.max(2.5, barW * ratio);
    const label = safeText(row.label).slice(0, 22);

    pdf.setTextColor(190, 210, 235);
    pdf.setFontSize(8);
    pdf.text(label, labelX, lineY + 4, { maxWidth: labelW });

    pdf.setFillColor(11, 26, 60);
    pdf.roundedRect(barX, lineY, barW, 6, 3, 3, "F");

    if (value < 0) {
      pdf.setFillColor(255, 110, 130);
      pdf.roundedRect(barX, lineY, fillW, 6, 3, 3, "F");
      pdf.setTextColor(255, 140, 155);
    } else {
      pdf.setFillColor(90, 230, 160);
      pdf.roundedRect(barX, lineY, fillW, 6, 3, 3, "F");
      pdf.setTextColor(120, 255, 180);
    }

    pdf.text(money(value), valueX, lineY + 4, { align: "right" });
  });
}

function drawSummaryPage(pdf, { session = {}, summaryData = {}, rows = [] }) {
  const data = deriveSummaryData(summaryData, rows);
  const pageW = 297;
  const pageH = 210;

  pdf.setFillColor(6, 10, 28);
  pdf.rect(0, 0, pageW, pageH, "F");

  pdf.setTextColor(110, 215, 255);
  pdf.setFontSize(10);
  pdf.text("MATRIXOS • MORPHEUS COUNT", 14, 16);

  const sectionName = safeText(
    session.sectionName || session.section || session.countSection || session.scopeName
  ).trim();
  const reportTitle = sectionName ? `Count Report - ${sectionName}` : "Count Report";
  const categoryNames = Array.isArray(session.categoryNames) ? session.categoryNames : (Array.isArray(session.meta?.categoryNames) ? session.meta.categoryNames : []);
  const categoryScopeLabel = categoryNames.length ? categoryNames.join(' • ') : (session.categoryScopeLabel || session.meta?.categoryScopeLabel || 'All Count Groups');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(26);
  pdf.text(reportTitle, 14, 29);

  pdf.setTextColor(180, 200, 230);
  pdf.setFontSize(11);
  pdf.text(`${safeText(session.storeName || session.store || "")} • ${safeText(session.id || "")}`, 14, 38);
  pdf.text(`Completed: ${safeText(session.completedAt || session.completedOn || "")}`, 14, 45);
  pdf.text(`Started by: ${safeText(session.startedBy || "")}`, 14, 52);

  const cardY = 62;
  const cardGap = 3;
  const metricCards = [
    { title: "Total Loss", value: money(data.totalLoss), width: 34, color: [255, 120, 120] },
    { title: "Total Gain", value: money(data.totalGain), width: 34, color: [120, 255, 180] },
    { title: "Net Difference", value: money(data.netDifference), width: 35, color: data.netDifference < 0 ? [255, 120, 120] : [120, 255, 180] },
    { title: "Counted", value: rows.length, width: 28 },
    { title: "Clean Lines", value: data.cleanLines, width: 31 },
    { title: "Variance Lines", value: data.varianceLines, width: 36 },
    { title: "Flags", value: data.flaggedItems, width: 25 },
    { title: "Notes", value: rows.filter((row) => getCountNote(row).trim()).length, width: 25 },
  ];

  let cardX = 14;
  metricCards.forEach((card) => {
    drawCard(pdf, cardX, cardY, card.width, 24, card.title, card.value, card.color);
    cardX += card.width + cardGap;
  });

  drawBarChart(pdf, 14, 94, 86, 95, data.chartRows);
  drawListCard(pdf, 105, 94, 86, 95, "Top 5 Losses", data.topLosses, false);
  drawListCard(pdf, 196, 94, 87, 95, "Top 5 Gains", data.topGains, true);

  pdf.setTextColor(110, 135, 165);
  pdf.setFontSize(8);
  pdf.text("MatrixOS Morpheus Count", 14, 202);
  pdf.text("Page 1", 283, 202, { align: "right" });
}

function pickFirstValue(row = {}, keys = []) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
}

function drawTablePages(pdf, { session = {}, rows = [], options = {} }) {
  pdf.addPage();

  autoTable(pdf, {
    startY: 22,
    head: [[
      "Product",
      "Category",
      "SKU",
      "Cost",
      "SOH",
      "Counted",
      "Waste",
      "Variance",
      "Cost Variance",
      "Notes",
    ]],
    body: buildGroupedTableBody(rows, session, options, (r) => [
      safeText(r.product),
      safeText(r.category),
      safeText(r.sku),
      money(r.cost),
      safeText(pickFirstValue(r, ["systemSoh", "realSoh", "system", "soh", "systemSOH"])),
      safeText(r.counted),
      safeText(r.waste),
      safeText(r.variance),
      money(r.costVariance),
      getCountNote(r),
    ], 10),
    theme: "striped",
    styles: {
      fontSize: 8,
      textColor: [20, 20, 20],
      cellPadding: 1.6,
      lineColor: [220, 226, 235],
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [25, 35, 52],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
    margin: { top: 22, right: 4, bottom: 12, left: 4 },
    willDrawPage: (data) => {
      const pageW = 297;
      const pageH = 210;
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageW, pageH, "F");
      pdf.setTextColor(20, 25, 35);
      pdf.setFontSize(18);
      pdf.text("Full Count Detail", 8, 11);
      pdf.setTextColor(90, 105, 125);
      pdf.setFontSize(9);
      pdf.text(`${rows.length} lines exported`, 8, 16);

      pdf.setTextColor(120, 130, 145);
      pdf.setFontSize(8);
      pdf.text(`${safeText(session.id || "")} • MatrixOS Morpheus Count`, 4, 204);
      pdf.text(`Page ${pdf.internal.getNumberOfPages()}`, 293, 204, { align: "right" });
    },
    didParseCell: (data) => {
      if (data.section === "head" && [2, 3, 4].includes(data.column.index)) {
        data.cell.styles.halign = "right";
      }
    },
    willDrawCell: (data) => {
      if (data.section === "body" && Array.isArray(data.row.raw) && data.row.raw.length > 1) {
        if (data.column.index === 7) {
          const variance = normalizeNumber(data.row.raw[7]);
          if (variance < 0) data.cell.styles.textColor = [220, 80, 90];
          if (variance > 0) data.cell.styles.textColor = [30, 170, 90];
        }
        if (data.column.index === 8) {
          const text = safeText(data.row.raw[8]);
          if (text.startsWith("-")) data.cell.styles.textColor = [220, 80, 90];
          else if (text !== "R0.00") data.cell.styles.textColor = [30, 170, 90];
        }
      }
    },
    columnStyles: {
      0: { cellWidth: 66, halign: "left" },
      1: { cellWidth: 31, halign: "left" },
      2: { cellWidth: 16, halign: "right" },
      3: { cellWidth: 15, halign: "right" },
      4: { cellWidth: 20, halign: "right" },
      5: { cellWidth: 16, halign: "center" },
      6: { cellWidth: 15, halign: "center" },
      7: { cellWidth: 17, halign: "center" },
      8: { cellWidth: 24, halign: "center" },
      9: { cellWidth: 69, halign: "left" },
    },
    tableWidth: 289,
  });
}

async function buildPdf(payload = {}) {
  const { session = {}, summaryData = {}, rows = [], options = {} } = payload;
  const pdf = new jsPDF("l", "mm", "a4");

  drawSummaryPage(pdf, { session, summaryData, rows });
  drawTablePages(pdf, { session, rows, options });

  return pdf;
}

export async function exportCountPDF(payload) {
  try {
    const pdf = await buildPdf(payload || {});
    const session = payload?.session || {};
    const fileBase = `${getSiteName(session)}-${getSessionDateCode(session)}-Count-Report-${getSectionName(session)}`;
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);

    window.open(url, "_blank", "noopener,noreferrer");

    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileBase}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (error) {
    console.error("PDF export failed", error);
    alert("PDF export failed. Please try again.");
  }
}

export const exportSessionCountPdf = exportCountPDF;

export async function exportMorpheusOrderSheetPdf(payload = {}) {
  try {
    const { sheet = {}, session = {}, rows = [], options = {} } = payload;
    const scope = session.storeName || session.store || sheet.storeName || sheet.storeKey || "ALL";
    const possibleScopes = [
      scope,
      session.storeName,
      session.store,
      session.storeKey,
      sheet.storeName,
      sheet.store,
      sheet.storeKey,
      "ALL",
    ].filter(Boolean);
    const scopedState = options.engineState || loadEngineState(scope) || loadEngineState("ALL") || {};
    const flagConfig = resolveFlagConfig(scope);
    const hideCounted = resolveMosBooleanOption(options.hideCountedInMosPdf, "hideCountedInMosPdf", possibleScopes) || Boolean(flagConfig.hideCountedInMosPdf);
    const hideZeroOrderItems = resolveMosBooleanOption(options.hideZeroOrderItemsInMosPdf, "hideZeroOrderItemsInMosPdf", possibleScopes) || Boolean(flagConfig.hideZeroOrderItemsInMosPdf);
    const sourceRows = Array.isArray(rows) ? rows : [];
    const pdfRows = hideZeroOrderItems ? sourceRows.filter(isOrderedRow) : sourceRows;
    const headColumns = hideCounted ? ["Product", "Category", "SKU", "Order", "Notes"] : ["Product", "Category", "SKU", "Counted", "Order", "Notes"];
    const bodyRows = buildGroupedTableBody(pdfRows, session, options, (r) => {
      const orderText = String(r.order ?? "").trim() ? r.order : "0";
      const base = [safeText(r.product), safeText(r.category), safeText(r.sku)];
      if (!hideCounted) base.push(safeText(r.counted));
      base.push(orderCell(orderText, r));
      base.push(safeText(r.orderNote || r.note));
      return base;
    }, headColumns.length);
    const pdf = new jsPDF("p", "mm", "a4");
    const pageW = 210;
    const pageH = 297;

    autoTable(pdf, {
      startY: 42,
      head: [headColumns],
      body: bodyRows,
      theme: "striped",
      styles: {
        fontSize: 8.5,
        textColor: [20, 20, 20],
        cellPadding: 2,
        lineColor: [220, 226, 235],
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [25, 35, 52],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { top: 42, right: 10, bottom: 14, left: 10 },
      columnStyles: hideCounted ? {
        0: { cellWidth: 58 },
        1: { cellWidth: 42 },
        2: { cellWidth: 18, halign: "right" },
        3: { cellWidth: 18, halign: "right" },
        4: { cellWidth: 54 },
      } : {
        0: { cellWidth: 55 },
        1: { cellWidth: 38 },
        2: { cellWidth: 18, halign: "right" },
        3: { cellWidth: 18, halign: "center" },
        4: { cellWidth: 18, halign: "right" },
        5: { cellWidth: 43 },
      },
      didParseCell: (data) => {
        const orderColumnIndex = hideCounted ? 3 : 4;
        if (data.section === "head" && (data.column.index === 2 || data.column.index === orderColumnIndex)) {
          data.cell.styles.halign = "right";
        }
        if (!hideCounted && data.section === "head" && data.column.index === 3) {
          data.cell.styles.halign = "center";
        }
      },
      willDrawPage: () => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageW, pageH, "F");
        pdf.setTextColor(20, 25, 35);
        pdf.setFontSize(18);
        pdf.text("Order Sheet", 10, 14);
        pdf.setTextColor(85, 100, 120);
        pdf.setFontSize(9);
        const exportedAt = sheet.createdAt ? new Date(sheet.createdAt).toLocaleString() : new Date().toLocaleString();
        pdf.text(`Store: ${safeText(sheet.storeName || session.storeName || session.store || "")}`, 10, 22);
        pdf.text(`Session: ${safeText(sheet.sessionId || session.sessionId || session.id || "")}`, 10, 28);
        pdf.text(`Started by: ${safeText(session.startedBy || sheet.startedBy || "")}   •   Exported by: ${safeText(sheet.createdBy || "")}`, 10, 34);
        pdf.text(`Date/Time: ${exportedAt}`, 10, 40);
        pdf.setTextColor(120, 130, 145);
        pdf.setFontSize(8);
        pdf.text("MatrixOS Morpheus Count", 10, 290);
        pdf.text(`Page ${pdf.internal.getNumberOfPages()}`, 200, 290, { align: "right" });
      },
    });

    const fileBase = `${getSiteName(session, sheet)}-${getSessionDateCode(session, sheet)}-Order-Sheet`;
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileBase}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (error) {
    console.error("Order sheet PDF export failed", error);
    alert("Order sheet PDF export failed. Please try again.");
  }
}
